import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GitBranch,
  Image as ImageIcon,
  Images,
  LoaderCircle,
  Plus,
  Send,
  Sparkles,
  Star,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface UploadedReference {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}

interface StudioResult {
  id: string;
  jobId: string | null;
  outputAttachmentId: string | null;
  status: 'PENDING' | 'GENERATING' | 'READY' | 'FAILED' | 'CANCELLED';
  providerKind: string | null;
  modelId: string | null;
  prompt: string;
  revision: number;
  selected: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

interface StudioProject {
  id: string;
  titleAr: string;
  modeKey: string;
  prompt: string;
  negativePrompt: string | null;
  settings: { size?: string; quality?: string } | null;
  updatedAt: string;
  results: StudioResult[];
}

export function GeneralStudioPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newProject, setNewProject] = useState({ titleAr: '', prompt: '' });
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [size, setSize] = useState<'1024x1024' | '1536x1024' | '1024x1536'>('1024x1024');
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('medium');
  const [references, setReferences] = useState<UploadedReference[]>([]);
  const [uploading, setUploading] = useState(false);

  const projectsQuery = useQuery({
    queryKey: ['studio-projects'],
    queryFn: () => api<StudioProject[]>('/api/studios/projects?mode=general_studio'),
  });
  const projectQuery = useQuery({
    queryKey: ['studio-project', selectedId],
    queryFn: () => api<StudioProject>(`/api/studios/projects/${selectedId}`),
    enabled: Boolean(selectedId),
    refetchInterval: (query) => {
      const project = query.state.data;
      return project?.results.some((result) =>
        ['PENDING', 'GENERATING'].includes(result.status),
      )
        ? 3_000
        : false;
    },
  });

  useEffect(() => {
    if (!selectedId && projectsQuery.data?.[0]) {
      setSelectedId(projectsQuery.data[0].id);
    }
  }, [selectedId, projectsQuery.data]);

  useEffect(() => {
    if (!projectQuery.data) return;
    setPrompt(projectQuery.data.prompt);
    setNegativePrompt(projectQuery.data.negativePrompt || '');
    if (projectQuery.data.settings?.size) {
      setSize(projectQuery.data.settings.size as typeof size);
    }
    if (projectQuery.data.settings?.quality) {
      setQuality(projectQuery.data.settings.quality as typeof quality);
    }
  }, [projectQuery.data]);

  const createProject = useMutation({
    mutationFn: () =>
      api<StudioProject>('/api/studios/projects', {
        method: 'POST',
        body: JSON.stringify(newProject),
      }),
    onSuccess: async (project) => {
      setSelectedId(project.id);
      setNewProject({ titleAr: '', prompt: '' });
      setShowCreate(false);
      toast.success('أُنشئ مشروع الاستوديو');
      await queryClient.invalidateQueries({ queryKey: ['studio-projects'] });
    },
    onError: (error) => toast.error(error.message),
  });

  const generate = useMutation({
    mutationFn: () =>
      api(`/api/studios/projects/${selectedId}/generate`, {
        method: 'POST',
        body: JSON.stringify({
          prompt,
          ...(negativePrompt ? { negativePrompt } : {}),
          referenceAttachmentIds: references.map((item) => item.id),
          size,
          quality,
        }),
      }),
    onSuccess: async () => {
      toast.success('أُضيف التوليد إلى الطابور');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['studio-project', selectedId] }),
        queryClient.invalidateQueries({ queryKey: ['studio-projects'] }),
        queryClient.invalidateQueries({ queryKey: ['jobs'] }),
      ]);
    },
    onError: (error) => toast.error(error.message),
  });

  const selectResult = useMutation({
    mutationFn: (id: string) =>
      api(`/api/studios/results/${id}/select`, { method: 'POST' }),
    onSuccess: async () => {
      toast.success('اعتُمدت النتيجة المختارة');
      await queryClient.invalidateQueries({ queryKey: ['studio-project', selectedId] });
    },
    onError: (error) => toast.error(error.message),
  });

  const branchResult = useMutation({
    mutationFn: (id: string) =>
      api<StudioProject>(`/api/studios/results/${id}/branch`, { method: 'POST' }),
    onSuccess: async (project) => {
      setSelectedId(project.id);
      toast.success('أُنشئ فرع جديد من النتيجة');
      await queryClient.invalidateQueries({ queryKey: ['studio-projects'] });
    },
    onError: (error) => toast.error(error.message),
  });

  async function uploadReferences(selected: FileList | null) {
    if (!selected?.length) return;
    if (references.length + selected.length > 4) {
      toast.error('الحد الأقصى 4 صور مرجعية');
      return;
    }
    setUploading(true);
    try {
      const uploaded: UploadedReference[] = [];
      for (const file of [...selected]) {
        const form = new FormData();
        form.append('file', file);
        uploaded.push(
          await api<UploadedReference>('/api/files/upload', {
            method: 'POST',
            body: form,
          }),
        );
      }
      setReferences((current) => [...current, ...uploaded]);
      toast.success('رُفعت الصور المرجعية مؤقتًا');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر رفع المراجع');
    } finally {
      setUploading(false);
    }
  }

  const project = projectQuery.data;
  return (
    <div className="grid min-h-screen lg:grid-cols-[280px_1fr]">
      <aside className="border-l border-ink/10 bg-white/60 p-4">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold text-slate-400">مساحة مرئية</p>
            <h1 className="mt-1 text-lg font-black">مشاريع الاستوديو</h1>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate((value) => !value)}
            className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-white hover:bg-teal"
            aria-label="مشروع جديد"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {showCreate && (
          <div className="mb-4 space-y-2 rounded-2xl border border-teal/20 bg-teal/5 p-3">
            <input value={newProject.titleAr} onChange={(event) => setNewProject({ ...newProject, titleAr: event.target.value })} placeholder="اسم المشروع" className="w-full rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal" />
            <textarea value={newProject.prompt} onChange={(event) => setNewProject({ ...newProject, prompt: event.target.value })} placeholder="الطلب الأساسي" rows={3} className="w-full resize-none rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] outline-none focus:border-teal" />
            <button type="button" onClick={() => createProject.mutate()} disabled={!newProject.titleAr || newProject.prompt.length < 3 || createProject.isPending} className="w-full rounded-lg bg-teal px-3 py-2 text-[10px] font-black text-white disabled:opacity-40">إنشاء المشروع</button>
          </div>
        )}

        <div className="space-y-1">
          {projectsQuery.data?.map((item) => (
            <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} className={`w-full rounded-xl px-3 py-3 text-start transition ${selectedId === item.id ? 'bg-ink text-white' : 'hover:bg-white'}`}>
              <p className="truncate text-xs font-black">{item.titleAr}</p>
              <p className={`mt-1 text-[9px] ${selectedId === item.id ? 'text-white/40' : 'text-slate-400'}`}>{item.results.length} نتيجة</p>
            </button>
          ))}
        </div>
      </aside>

      <section className="p-4 sm:p-7">
        {!project ? (
          <div className="grid min-h-[70vh] place-items-center text-center">
            <div>
              <Images className="mx-auto mb-4 h-10 w-10 text-teal" />
              <h2 className="text-xl font-black">ابدأ مشروعًا بصريًا</h2>
              <p className="mt-2 text-xs font-semibold text-slate-500">أنشئ مشروعًا ثم أضف الطلب والمراجع لتوليد النتائج.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-black text-teal">الاستوديو العام</p>
                <h2 className="mt-1 text-2xl font-black">{project.titleAr}</h2>
                <p className="mt-1 text-[10px] font-semibold text-slate-400">{project.results.length} نتيجة · آخر تحديث {new Intl.DateTimeFormat('ar-SA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(project.updatedAt))}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[9px] font-bold text-amber-700">
                يتطلب مفتاح OpenAI ونموذج صور مهيأ
              </div>
            </header>

            <div className="grid gap-5 xl:grid-cols-[390px_1fr]">
              <div className="space-y-4">
                <section className="rounded-2xl border border-ink/10 bg-white p-4">
                  <label className="block">
                    <span className="mb-2 block text-[10px] font-black text-slate-500">الطلب / Prompt</span>
                    <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={7} className="w-full resize-y rounded-xl border border-ink/10 bg-slate-50 px-3 py-3 text-xs leading-6 outline-none focus:border-teal" />
                  </label>
                  <label className="mt-3 block">
                    <span className="mb-2 block text-[10px] font-black text-slate-500">قيود سلبية</span>
                    <textarea value={negativePrompt} onChange={(event) => setNegativePrompt(event.target.value)} rows={3} className="w-full resize-none rounded-xl border border-ink/10 bg-slate-50 px-3 py-2 text-xs outline-none focus:border-teal" placeholder="ما يجب تجنبه في الصورة" />
                  </label>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <select value={size} onChange={(event) => setSize(event.target.value as typeof size)} className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-[10px] outline-none focus:border-teal">
                      <option value="1024x1024">مربع</option>
                      <option value="1536x1024">أفقي</option>
                      <option value="1024x1536">رأسي</option>
                    </select>
                    <select value={quality} onChange={(event) => setQuality(event.target.value as typeof quality)} className="rounded-xl border border-ink/10 bg-slate-50 px-3 py-2.5 text-[10px] outline-none focus:border-teal">
                      <option value="low">اقتصادي</option>
                      <option value="medium">متوازن</option>
                      <option value="high">دقة عالية</option>
                    </select>
                  </div>
                </section>

                <section className="rounded-2xl border border-ink/10 bg-white p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-black">الصور المرجعية</h3>
                    <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg bg-teal/10 px-2.5 py-1.5 text-[9px] font-black text-teal">
                      {uploading ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      إضافة
                      <input type="file" multiple accept=".png,.jpg,.jpeg" className="hidden" onChange={(event) => void uploadReferences(event.target.files)} />
                    </label>
                  </div>
                  {references.length ? (
                    <div className="grid grid-cols-2 gap-2">
                      {references.map((reference) => (
                        <div key={reference.id} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100">
                          <img src={`/api/files/${reference.id}?inline=1`} alt={reference.originalName} className="h-full w-full object-cover" />
                          <button type="button" onClick={() => setReferences((current) => current.filter((item) => item.id !== reference.id))} className="absolute end-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-lg bg-ink/70 text-white"><X className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="rounded-xl border border-dashed border-ink/10 py-8 text-center text-[10px] font-bold text-slate-400">لا توجد صور مرجعية.</p>
                  )}
                </section>

                <button type="button" onClick={() => generate.mutate()} disabled={!prompt.trim() || generate.isPending} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-sm font-black text-white hover:bg-teal disabled:opacity-40">
                  {generate.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  توليد نتيجة جديدة
                </button>
              </div>

              <section>
                {project.results.length ? (
                  <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                    {project.results.map((result) => (
                      <article key={result.id} className={`overflow-hidden rounded-2xl border bg-white ${result.selected ? 'border-gold ring-2 ring-gold/15' : 'border-ink/10'}`}>
                        <div className="relative aspect-square bg-slate-100">
                          {result.outputAttachmentId ? (
                            <img src={`/api/files/${result.outputAttachmentId}?inline=1`} alt={`نتيجة ${result.revision}`} className="h-full w-full object-cover" />
                          ) : (
                            <div className="grid h-full place-items-center">
                              {result.status === 'FAILED' ? <X className="h-7 w-7 text-red-400" /> : <LoaderCircle className="h-7 w-7 animate-spin text-teal" />}
                            </div>
                          )}
                          {result.selected && (
                            <span className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-gold px-2 py-1 text-[8px] font-black text-white"><Star className="h-2.5 w-2.5 fill-current" /> مختارة</span>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[10px] font-black">النتيجة {result.revision}</p>
                            <span className="text-[8px] font-bold text-slate-400">{result.modelId || result.status}</span>
                          </div>
                          <div className="mt-3 flex gap-1.5">
                            {result.outputAttachmentId && (
                              <button type="button" onClick={() => selectResult.mutate(result.id)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gold/10 px-2 py-2 text-[9px] font-black text-gold"><Star className="h-3 w-3" /> اعتماد</button>
                            )}
                            <button type="button" onClick={() => branchResult.mutate(result.id)} className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-teal/10 px-2 py-2 text-[9px] font-black text-teal"><GitBranch className="h-3 w-3" /> فرع</button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="grid min-h-[55vh] place-items-center rounded-3xl border border-dashed border-ink/15 bg-white/40 text-center">
                    <div>
                      <ImageIcon className="mx-auto mb-3 h-9 w-9 text-teal" />
                      <p className="text-sm font-black">لم تُولد نتائج بعد</p>
                      <p className="mt-1 text-[10px] font-semibold text-slate-500">أضف المراجع واضبط Prompt ثم ابدأ التوليد.</p>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
